import styled from '@emotion/styled';
import { Upload } from 'antd';
import type { UploadProps } from 'antd/lib';
import type { PropsWithChildren } from 'react';
import React from 'react';

import { renderAllowedMediaTooltip } from 'components/common/UploadModal/utils/renderAllowedMediaTooltip';
import colors from 'config/theme/colors';
import useMediaRules from 'hooks/useMediaRules';
import type { AllowedMedias } from 'hooks/useMediaRules';
import { Loader } from 'ui/base/index';
import Tooltip from 'ui/base/Tooltip';

import Dragger from './Dragger';

export const Dropbox = styled('div', { target: 'fb-upload-dropbox' })<{height: string, width: string}>`
  height: ${props => props.height};
  width: ${props => props.width};
  background-color: #f1f6ff;
  background: linear-gradient(180deg, #fafcff 0%, #f1f6ff 100%);
  display: flex;
  border: 1px dashed ${colors['@grey-20']};
  justify-content: center;
  position: relative;
  border-radius: 6px;
  cursor: pointer;
  flex-direction: column;
  align-items: center;

  &:hover {
    border-color: #238fdb;
  }

  .fb-icon--plus-outline {
    color: ${colors['@bright-navy-blue']};
    border: 10px solid #fff;
    background-color: #fff;
    border-radius: 50%;
    font-size: 30px;
  }
  .fb-icon--info {
    padding: 0 10px 10px 10px;
    position: absolute;
    color: ${colors['@text-color-secondary']};
    bottom: 0;
    right: 0;
  }
`;

/**
 * Upload component with integrated media rules and multiple display modes.
 *
 * This component wraps Ant Design's Upload component to provide:
 * - Integration with the media rules system via `useMediaRules` hook
 * - Visual card mode with drag & drop area
 * - Loading states with spinner
 * - Automatic file type validation and tooltip generation
 *
 * @example
 * // Card mode with drag & drop area
 * <Upload
 *   showCard
 *   width="200px"
 *   height="150px"
 *   allowed={{ path: ['campaign', 'banner'] }}
 *   beforeUpload={handleUpload}
 * />
 *
 * @example
 * // Simple upload with custom button
 * <Upload
 *   allowed={{ mediaTypes: [MEDIA_TYPE.IMAGE] }}
 *   beforeUpload={handleUpload}
 * >
 *   <Button>Upload Image</Button>
 * </Upload>
 */

/** @type {React.FC<FBUploadProps & import("antd").UploadProps>} */

type FBUploadProps = {
  limit: number;
  width?: string;
  height?: string;
  loading?: boolean;
  showCard?: boolean;
  allowed?: AllowedMedias;
  displayMaxSizeMessage?: boolean;
} & PropsWithChildren & UploadProps;

const FBUpload: React.FC<FBUploadProps> = ({ allowed, loading, showCard, children, width = '144px', height = '160px', displayMaxSizeMessage = false, ...rest }) => {
  const { extensions, config, fileTypes: supportedType } = useMediaRules(allowed);

  const types = supportedType && (Array.isArray(supportedType) ? supportedType : [supportedType]);

  const tooltip = renderAllowedMediaTooltip(extensions, config, displayMaxSizeMessage);
  const displayToolTip = showCard && tooltip;
  const displayLoader = loading && !showCard;

  return (
    <Upload showUploadList={false} accept={types?.join(',')} disabled={loading} {...rest}>
      {displayLoader && <Loader />}
      {showCard && (
        <Dropbox height={height} width={width}>
          {!loading && <i className="fb-icon--plus-outline" />}
          {loading && <Loader />}
          {displayToolTip && (
            <Tooltip title={tooltip}>
              <i className="fb-icon--info" />
            </Tooltip>
          )}
        </Dropbox>
      )}
      {!loading && children}
    </Upload>
  );
};

export default Object.assign(FBUpload, { Dragger });
